import React, {useEffect, useState} from "react";
import { collection, getDocs, limit, orderBy, query, where } from "@firebase/firestore";
import { db } from "../../firebase/FirebaseProvider";
import { PHOTOS_LIMIT, logDataLoaded } from "../util/Util";
import {Photo, Session, User} from "../types/Types";
import HubNav from "./HubNav";
import PageTracking from "../auth/PageTracking";
import PhotoTagsFilter from "./PhotoTagsFilter";
import MostLikedPhotos from "./MostLikedPhotos";
import RecentPhotos from "./RecentPhotos";
import PhotoHistory from "./PhotoHistory";
import {getDisplayPhotoTag} from "../tag/PhotoTag";

interface Props {
    usersMap: Map<string, User>;
    sessionsMap: Map<string, Session>;
    currentUser: User | undefined;
    likesMap: Map<string, boolean>;
    photos: Array<Photo>;
    setPhotos: (photos: Array<Photo>) => void;
}

export interface PhotoTagDetails {
    tagName: string;
    tagCount: number;
}

function Photos(props: Props) {

    const [photos, setPhotos] = useState<Array<Photo>>(props.photos);
    const [loadingPhotos, setLoadingPhotos] = useState<boolean>(true);
    const [tags, setTags] = useState<Array<PhotoTagDetails>>([]);
    const [isLoadingTags, setIsLoadingTags] = useState<boolean>(true);

    useEffect(() => {
        // Load photos when component mounts
        const loadPhotos = async () => {
            if (props.photos.length > 0) {
                // Use cached photos if available
                setPhotos(props.photos);
                setLoadingPhotos(false);
                return;
            }

            try {
                setLoadingPhotos(true);
                const photosCollection = query(
                    collection(db, 'photos'),
                    where('hidden', '==', false),
                    orderBy('createdAt', 'desc')
                );
                const photosSnapshot = await getDocs(photosCollection);
                const photosArray: Photo[] = [];
                photosSnapshot.forEach((doc) => {
                    const photo = doc.data() as Photo;
                    photo.id = doc.id;
                    photosArray.push(photo);
                });
                logDataLoaded('all photos', photosArray.length);
                setPhotos(photosArray);
                props.setPhotos(photosArray); // Cache in parent
            } catch (error) {
                console.error('Error loading photos:', error);
            } finally {
                setLoadingPhotos(false);
            }
        };

        loadPhotos();
    }, []);

    useEffect(() => {
        if (!photos || photos.length === 0) {
            setIsLoadingTags(true);
            return;
        }
        const tagCount: { [key: string]: number } = {};
        photos.forEach(photo => {
            photo.tags.forEach(tag => {
                const displayTag = getDisplayPhotoTag(tag);
                if (displayTag in tagCount) {
                    tagCount[displayTag]++;
                } else {
                    tagCount[displayTag] = 1;
                }
            });
        });
        const sortedTags = Object.entries(tagCount)
            .filter(([tagName]) => tagName !== 'unknown')
            .sort((a, b) => b[1] - a[1])
            .map(entry => (
                {tagName: entry[0], tagCount: entry[1]} as PhotoTagDetails
            ));
        setTags(sortedTags);
        setIsLoadingTags(false);
    }, [photos]);

    return (
        <>
            <PageTracking />
            <HubNav currentUser={props.currentUser} />
            <PhotoTagsFilter 
                tags={tags} 
                totalPhotos={photos.length} 
                isLoading={isLoadingTags || loadingPhotos}
            />
            <RecentPhotos 
                usersMap={props.usersMap}
                sessionsMap={props.sessionsMap}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
            />
            <MostLikedPhotos 
                usersMap={props.usersMap}
                sessionsMap={props.sessionsMap}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
            />
            <PhotoHistory 
                usersMap={props.usersMap}
                sessionsMap={props.sessionsMap}
                photos={photos}
            />
        </>
    )
}

export default Photos;
